import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FileText,
    Clock,
    IndianRupee,
    Calendar,
    Building2,
    Package,
    XCircle,
    ShoppingCart
} from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import AttachmentModal from '../../components/Inbox/AttachmentModal';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import VerificationInput from '../../components/Inbox/VerificationInput';

import {
    fetchVerificationSPPOClose,
    fetchSPPOByNoForVerify,
    approveCloseSPPO,
    setSelectedSppoNo,
    setSelectedCCCode,
    setSelectedVendorCode,
    resetSPPOData,
    clearApprovalResult,
    selectVerificationSPPOCloseArray,
    selectSPPOData,
    selectVerificationSPPOCloseLoading,
    selectSPPODataLoading,
    selectApproveCloseSPPOLoading,
    selectVerificationSPPOCloseError,
    selectSPPODataError,
    selectApproveCloseSPPOError,
    selectSelectedSppoNo,
    selectApprovalResult,
    setSelectedRoleId,
    setSelectedUserId,
    setSelectedType
} from '../../slices/spPOSlice/closeSPPOSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton
} from '../../slices/CommonSlice/getStatusSlice';

import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

// ============================================================================
// HELPER FUNCTION - Extract type from notification path
// ============================================================================
const getTypeFromPath = (notificationData) => {
    const path = notificationData?.NavigationPath || '';
    
    // Extract type from URL query parameter
    // Example: /purchase/verifysppoclose?closetype=performing
    if (path.toLowerCase().includes('closetype=performing')) {
        return 'Performing';
    } else if (path.toLowerCase().includes('closetype=non-performing')) {
        return 'Non-Performing';
    }
    
    // Default to Performing if not specified
    return 'Performing';
};

const VerifySPPOClose = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();

    // Selectors
    const sppoCloseList = useSelector(selectVerificationSPPOCloseArray);
    const sppoCloseLoading = useSelector(selectVerificationSPPOCloseLoading);
    const sppoCloseError = useSelector(selectVerificationSPPOCloseError);

    const sppoData = useSelector(selectSPPOData);
    const sppoDataLoading = useSelector(selectSPPODataLoading);
    const sppoDataError = useSelector(selectSPPODataError);

    const approvalLoading = useSelector(selectApproveCloseSPPOLoading);
    const selectedSppoNo = useSelector(selectSelectedSppoNo);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const { userData, userDetails } = useSelector((state) => state.auth);
    const roleId = userData?.roleId || userData?.RID;
    const uid = userData?.UID || userData?.uid;

    // Local State
    const [selectedItem, setSelectedItem] = useState(null);
    const [isVerified, setIsVerified] = useState(false);
    const [verificationComment, setVerificationComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCCCode, setFilterCCCode] = useState('All');
    const [filterVendor, setFilterVendor] = useState('All');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);
    const [attachmentUrl, setAttachmentUrl] = useState('');
    const [closeType, setCloseType] = useState('Performing'); // Track current type

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const ccCodes = [...new Set(sppoCloseList.map(item => item.CCCode))].filter(Boolean);
    const vendors = [...new Set(sppoCloseList.map(item => item.VendorName))].filter(Boolean);

    const getCurrentUser = () => {
        return userData?.userName || userDetails?.userName || 'system';
    };

    const getCurrentRoleName = () => {
        return userDetails?.roleName || userData?.roleName ||
            notificationData?.InboxTitle ||
            notificationData?.ModuleDisplayName ||
            'SPPO Close Verifier';
    };

    const formatApprovalComment = (roleName, userName, comment) => {
        return `${roleName} : ${userName} : ${comment}`;
    };

    const updateRemarksHistory = (existingRemarks, newRoleName, newUserName, newComment) => {
        const formattedNewComment = formatApprovalComment(newRoleName, newUserName, newComment);
        if (!existingRemarks || existingRemarks.trim() === '') {
            return formattedNewComment;
        }
        return `${existingRemarks.trim()}||${formattedNewComment}`;
    };

    const handleViewAttachment = (filePath) => {
        if (!filePath) {
            toast.error('No attachment available');
            return;
        }
        
        // Construct S3 URL - adjust based on your S3 config
        const fullUrl = `https://your-s3-bucket.s3.amazonaws.com/sppo/${filePath}`;
        
        if (!fullUrl) {
            toast.error('Invalid file path');
            return;
        }
        
        setAttachmentUrl(fullUrl);
        setShowAttachmentModal(true);
    };

    // Initialize - Extract type from path and fetch data
    useEffect(() => {
        if (roleId && uid) {
            // Extract type from notification data path
            const typeValue = getTypeFromPath(notificationData);
            setCloseType(typeValue);
            
            
            dispatch(setSelectedRoleId(roleId));
            dispatch(setSelectedUserId(uid));
            dispatch(setSelectedType(typeValue));
            dispatch(fetchVerificationSPPOClose({ 
                roleId, 
                userId: uid, 
                type: typeValue 
            }));
        }
    }, [roleId, uid, notificationData, dispatch]);

    useEffect(() => {
        // Legacy VerifySPPOCloseView offers only Verify / Approve / Reject.
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetSPPOData());
            dispatch(resetApprovalData());
            dispatch(clearApprovalResult());
        };
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem?.SPPONo && selectedItem?.CCCode && selectedItem?.VendorCode) {

            dispatch(setSelectedSppoNo(selectedItem.SPPONo));
            dispatch(setSelectedCCCode(selectedItem.CCCode));
            dispatch(setSelectedVendorCode(selectedItem.VendorCode));
            
            dispatch(fetchSPPOByNoForVerify({ 
                sppoNo: selectedItem.SPPONo,
                ccCode: selectedItem.CCCode,
                vendorCode: selectedItem.VendorCode
            }));

            setIsVerified(false);
            setVerificationComment('');
            setShowRemarksHistory(false);
        }
    }, [selectedItem, dispatch]);

    useEffect(() => {
        if (selectedItem && roleId && sppoData) {
            const moid = sppoData?.MOID;
            if (moid) {
                dispatch(fetchStatusList({
                    MOID: moid,
                    ROID: roleId,
                    // Legacy requests the status list with ChkAmt 0.
                    ChkAmt: 0
                }));
            }
        }
    }, [selectedItem, roleId, sppoData, dispatch]);

    useEffect(() => {
        if (selectedItem && sppoData) {
            const moid = sppoData?.MOID;
            if (moid) {
                dispatch(setSelectedMOID(moid));
                dispatch(fetchRemarks({
                    trno: sppoData.SPPONo || selectedItem.SPPONo || '',
                    moid: moid
                }));
            }
        }
    }, [selectedItem, sppoData, dispatch]);

    useEffect(() => {
        if (selectedItem) {
            setIsLeftPanelCollapsed(true);
        }
    }, [selectedItem]);

    const handleBackToInbox = () => {
        if (onNavigate) {
            onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
        }
    };

    const handleRefresh = () => {
        if (roleId && uid) {
            const typeValue = getTypeFromPath(notificationData);
            
            
            dispatch(fetchVerificationSPPOClose({ 
                roleId, 
                userId: uid, 
                type: typeValue 
            }));
            
            if (selectedItem) {
                dispatch(fetchSPPOByNoForVerify({ 
                    sppoNo: selectedItem.SPPONo,
                    ccCode: selectedItem.CCCode,
                    vendorCode: selectedItem.VendorCode
                }));
            }
        }
    };

    const handleItemSelect = (item) => {
        setSelectedItem(item);
    };

    const buildSPPOCloseApprovalPayload = (actionValue) => {
        const currentUser = getCurrentUser();
        const currentRoleName = getCurrentRoleName();

        const updatedRemarks = updateRemarksHistory(
            sppoData?.POCloseRemarks || '',
            currentRoleName,
            currentUser,
            verificationComment.trim()
        );

        // Get current date in dd-MMM-yyyy format
        const currentDate = new Date().toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });

        const payload = {
            VendorCode: sppoData?.VendorCode || selectedItem?.VendorCode || '',
            SPPONo: sppoData?.SPPONo || selectedItem?.SPPONo || '',
            CCCode: sppoData?.CCCode || selectedItem?.CCCode || '',
            // Legacy: closing date = the requested close date (verifier may change it); note = this verifier's note.
            POCloseDate: sppoData?.POCloseDate || currentDate,
            POCloseRemarks: verificationComment.trim(),
            RoleId: roleId,
            CreatedBy: currentUser,
            Action: actionValue
        };

        return payload;
    };

    const handleActionClick = async (action) => {
        if (!selectedItem) {
            toast.error('No SPPO selected');
            return;
        }

        if (!verificationComment || verificationComment.trim() === '') {
            toast.error('Verification comment is mandatory. Please add your comments before proceeding.');
            return;
        }

        if (!isVerified) {
            toast.error('Please verify the SPPO close details by checking the verification checkbox.');
            return;
        }

        let actionValue = action.value || action.text || action.type;
        
        if (!actionValue || actionValue.trim() === '') {
            const typeToValueMap = {
                'approve': 'Approve',
                'verify': 'Verify',
                'reject': 'Reject',
                'return': 'Return'
            };
            actionValue = typeToValueMap[action.type?.toLowerCase()] || 'Verify';
        }

        try {
            const payload = buildSPPOCloseApprovalPayload(actionValue);

            const result = await dispatch(approveCloseSPPO(payload)).unwrap();

            // API returns ResponseResult { Data: <SP status> }; spApproveSppoClose's success literal is "Submited".
            const status = typeof result === 'string' ? result : result?.Data;
            if (status !== 'Submited' && status !== 'Submitted') {
                toast.error(status || 'Error Occurred While Serivice Provider Closing', { autoClose: 10000 });
                return;
            }
            toast.success(`${action.text || actionValue} completed successfully!`);

            setTimeout(() => {
                const typeValue = getTypeFromPath(notificationData);
                dispatch(fetchVerificationSPPOClose({ 
                    roleId, 
                    userId: uid, 
                    type: typeValue 
                }));
                setSelectedItem(null);
                setVerificationComment('');
                setIsVerified(false);
                setShowRemarksHistory(false);
                setIsLeftPanelCollapsed(false);
                dispatch(resetSPPOData());
                dispatch(resetApprovalData());
                dispatch(clearApprovalResult());
            }, 1000);

        } catch (error) {
            
            let errorMessage = `Failed to ${action.text?.toLowerCase() || actionValue.toLowerCase()}`;

            if (error && typeof error === 'string') {
                errorMessage = error;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            toast.error(errorMessage, { autoClose: 10000 });
        }
    };

    const filteredItems = sppoCloseList.filter(item => {
        const matchesSearch = searchQuery === '' ||
            item.SPPONo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.VendorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.CCCode?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCCCode = filterCCCode === 'All' || item.CCCode === filterCCCode;
        const matchesVendor = filterVendor === 'All' || item.VendorName === filterVendor;

        return matchesSearch && matchesCCCode && matchesVendor;
    });

    const renderItemCard = (item, isSelected) => {
        return (
            <div className="p-4">
                <div className="flex items-center space-x-3 mb-3">
                    <div className="relative">
                        <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-800/50 dark:to-purple-800/50 flex items-center justify-center">
                            <ShoppingCart className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-orange-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                             {item.VendorName} -{item.SPPONo} 
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {item.vendorCode}
                        </p>
                    </div>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    <div className="flex items-center justify-between">
                        <span className="flex items-center space-x-1">
                            <Building2 className="w-3 h-3" />
                            <span>{item.CCCode}</span>
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="flex items-center space-x-1">
                            <IndianRupee className="w-3 h-3" />
                            <span>₹{formatIndianCurrency(item.Balance || 0)}</span>
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[160px]">{item.VendorName} - {item.SPPONo}</span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[100px]">
                <Building2 className="w-3 h-3" />{item.CCCode}
            </span>
            <span className="ml-auto font-bold text-indigo-600 dark:text-indigo-400 whitespace-nowrap">₹{formatIndianCurrency(item.Balance || 0)}</span>
        </div>
    );

    const renderCollapsedItem = (item, isSelected) => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-800/50 dark:to-purple-800/50 flex items-center justify-center">
            <ShoppingCart className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        const displayData = sppoData || selectedItem;
        const hasDetailedData = !!sppoData;

        return (
            <div className="space-y-4">
                {sppoDataLoading && (
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 border border-indigo-200 dark:border-indigo-700">
                        <div className="flex items-center space-x-3">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                            <span className="text-indigo-700 dark:text-indigo-400 text-sm">
                                Loading detailed information...
                            </span>
                        </div>
                    </div>
                )}

                {/* CUSTOM HEADER */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                            <div className="relative">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                                    <ShoppingCart className="w-8 h-8 text-white" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-orange-500 rounded-full border-3 border-white dark:border-gray-800 flex items-center justify-center">
                                    <XCircle className="w-4 h-4 text-white" />
                                </div>
                            </div>

                            <div className="flex-1">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                    {displayData.VendorName}
                                </h2>
                                <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">
                                    SPPO Number:# {displayData.SPPONo} 
                                </p>

                                <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">
                                        Service PO
                                    </span>
                                    <span className={`px-3 py-1 ${
                                        closeType === 'Performing' 
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                    } rounded-full text-xs font-medium`}>
                                        {closeType}
                                    </span>
                                    <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-xs font-medium">
                                        Status: {displayData.Status}
                                    </span>
                                    {displayData.VendorName && (
                                        <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                                            {displayData.VendorName}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {hasDetailedData && (
                            <div className="text-right">
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Closing Balance</p>
                                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                                    ₹{formatIndianCurrency(sppoData.ClosingBalance || 0)}
                                </p>
                                <div className="mt-2 space-y-1">
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                        Total Value: ₹{formatIndianCurrency(sppoData.TotalValue || 0)}
                                    </p>
                                    {sppoData.POCloseDate && (
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                            Close Date: {sppoData.POCloseDate}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-indigo-200 dark:border-indigo-700">
                        {hasDetailedData && sppoData.MOID && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">MOID</p>
                                <p className="text-sm font-bold text-gray-900 dark:text-white">
                                    {sppoData.MOID}
                                </p>
                            </div>
                        )}
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">SPPO No</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                {displayData.SPPONo}
                            </p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Vendor Code</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                {displayData.VendorCode}
                            </p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cost Center</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                {displayData.CCCode}
                            </p>
                        </div>
                    </div>
                </div>

                {/* PO Period */}
                {hasDetailedData && (sppoData.SPPOStartDate || sppoData.SPPOEndDate) && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                            <Calendar className="w-5 h-5 text-indigo-600" />
                            <span>PO Period</span>
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Start Date</p>
                                <p className="text-base font-semibold text-gray-900 dark:text-white">
                                    {sppoData.SPPOStartDate || 'N/A'}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">End Date</p>
                                <p className="text-base font-semibold text-gray-900 dark:text-white">
                                    {sppoData.SPPOEndDate || 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Item Details */}
                {hasDetailedData && sppoData.ItemDescList && sppoData.ItemDescList.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                                    <Package className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                    Item Details
                                </h3>
                            </div>
                            {sppoData.FilePath && (
                                <button
                                    onClick={() => handleViewAttachment(sppoData.FilePath)}
                                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all text-sm font-medium"
                                >
                                    View Document
                                </button>
                            )}
                        </div>

                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                            Description
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                            Unit
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                            Quantity
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                            Rate
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                            Client Rate
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {sppoData.ItemDescList.map((item, index) => (
                                        <tr key={item.SPPOItemId || index} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                                {item.Description}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-center font-medium text-gray-900 dark:text-white">
                                                {item.Unit}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-center font-medium text-gray-900 dark:text-white">
                                                {item.Quantity}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right font-medium text-indigo-700 dark:text-indigo-400">
                                                ₹{formatIndianCurrency(item.Rate || 0)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right font-medium text-purple-700 dark:text-purple-400">
                                                ₹{formatIndianCurrency(item.ClientRate || 0)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Remarks Section */}
                {hasDetailedData && sppoData.Remarks && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                            <FileText className="w-5 h-5 text-indigo-600" />
                            <span>PO Remarks</span>
                        </h3>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {sppoData.Remarks}
                            </p>
                        </div>
                    </div>
                )}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <VerificationInput
                    isVerified={isVerified}
                    onVerifiedChange={setIsVerified}
                    comment={verificationComment}
                    onCommentChange={(e) => setVerificationComment(e.target.value)}
                    config={{
                        checkboxLabel: '✓ I have verified all SPPO Close details',
                        checkboxDescription: 'Including vendor information, closing balance, item details, and supporting documentation',
                        commentLabel: 'Verification Comments',
                        commentPlaceholder: 'Please verify SPPO details, closing balance, vendor information, and reason for closure...',
                        commentRequired: true,
                        commentMaxLength: 1000,
                        showCharCount: true,
                        validationStyle: 'dynamic',
                        checkboxGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
                        commentGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        commentBorder: 'border-indigo-200 dark:border-indigo-700'
                    }}
                />

                {statusLoading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center space-x-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                            <span className="text-gray-600 dark:text-gray-400">Loading actions...</span>
                        </div>
                    </div>
                ) : statusError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                        <p className="text-red-600 dark:text-red-400 text-center">
                            ⚠️ Error loading actions: {statusError}
                        </p>
                    </div>
                ) : !hasActions || !enabledActions || enabledActions.length === 0 ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                        <p className="text-yellow-700 dark:text-yellow-400 text-center">
                            ℹ️ No actions available for this SPPO close request
                        </p>
                    </div>
                ) : (
                    <ActionButtons
                        actions={enabledActions}
                        onActionClick={handleActionClick}
                        loading={approvalLoading}
                        isVerified={isVerified}
                        comment={verificationComment}
                        showValidation={true}
                        excludeActions={['send back']}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'SPPO Close'} (${sppoCloseList.length})`}
                subtitle={ModuleDisplayName}
                itemCount={sppoCloseList.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={ShoppingCart}
                badgeText={`SPPO Close - ${closeType}`}
                badgeCount={sppoCloseList.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by SPPO no, vendor, CC code...',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value)
                }}
                filters={[
                    {
                        value: filterCCCode,
                        onChange: (e) => setFilterCCCode(e.target.value),
                        defaultLabel: 'All Cost Centers',
                        options: ccCodes
                    },
                    {
                        value: filterVendor,
                        onChange: (e) => setFilterVendor(e.target.value),
                        defaultLabel: 'All Vendors',
                        options: vendors
                    }
                ]}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredItems,
                    selectedItem: selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem: renderListItem,
                    renderCollapsedItem: renderCollapsedItem,
                    loading: sppoCloseLoading,
                    error: sppoCloseError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No SPPO Close requests found!',
                        itemKey: 'SPPONo',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20'
                    },
                    renderPopupContent: (_item) => renderDetailContent(),
                    popupConfig: {
                        title: 'SPPO Close Verification',
                        icon: ShoppingCart,
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem: selectedItem,
                    loading: sppoDataLoading,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'SPPO Details',
                        icon: ShoppingCart,
                        selectedTitle: 'SPPO Close Verification',
                        emptyTitle: 'No SPPO Selected',
                        emptyMessage: 'Select a SPPO from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        maxHeight: 'calc(100vh - 200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />

            <AttachmentModal
                isOpen={showAttachmentModal}
                onClose={() => setShowAttachmentModal(false)}
                fileUrl={attachmentUrl}
                title="SPPO Document"
            />
        </div>
    );
};

export default VerifySPPOClose;